/* eslint-disable @typescript-eslint/no-dynamic-delete */

import { v4 as uuid } from 'uuid';
import React, { createContext, useContext, useMemo, useRef, useState } from 'react';

type ToastType = 'neutral' | 'info' | 'success' | 'error' | 'warning';

type ToastState = 'in' | 'idle' | 'out';

interface ToastOptions {
	icon?: string;
	color?: string;

	type?: ToastType;
	state?: ToastState;

	className?: string;

	inDuration?: number;
	idleDuration?: number;
	outDuration?: number;

	closable?: boolean;
	autoClose?: boolean;

	translate?: boolean;

	actions?: ToastAction[];
}

interface ToastAction {
	title?: string;
	color?: string;

	url?: string;
	handler?: React.MouseEventHandler<HTMLLIElement>;

	className?: string;

	disabled?: boolean;
}

interface Toast {
	id: string;

	title: string;
	message: string;

	options: ToastOptions;
}

interface ToastFunction {
	(title: string, message: string, options?: ToastOptions): void;
	neutral: (title: string, message: string, options?: ToastOptions) => void;
	info: (title: string, message: string, options?: ToastOptions) => void;
	success: (title: string, message: string, options?: ToastOptions) => void;
	warning: (title: string, message: string, options?: ToastOptions) => void;
	error: (title: string, message: string, options?: ToastOptions) => void;
}

const defaultToastOptions: ToastOptions = {
	type: 'neutral',
	state: 'in',

	inDuration: 500,
	idleDuration: 5000,
	outDuration: 500,

	closable: true,
	autoClose: true,

	translate: false,
	actions: [],
};

const defaultToastIcons = {
	neutral: 'flag-regular',
	info: 'circle-info-regular',
	success: 'circle-check-regular',
	warning: 'square-exclamation-regular',
	error: 'triangle-exclamation-regular',
};

const ToastContext = createContext(null as any);

export function ToastProvider({ children }) {
	const [toasts, setToasts] = useState([] as Toast[]);
	const timeoutsRef = useRef({});

	function addToast(title: string, message: string, options: ToastOptions = {}) {
		const id = uuid();
		const type = options.type ?? defaultToastOptions.type ?? 'neutral';
		const icon = options.icon ?? defaultToastIcons[type];

		const newToast: Toast = {
			id,
			title,
			message,
			options: {
				...defaultToastOptions,
				...options,
				type,
				icon,
				state: 'in',
			},
		};

		setToasts((currentToasts) => [...currentToasts, newToast]);

		timeoutsRef.current[`add-in-${id}`] = setTimeout(() => {
			restateToast(id, 'idle');

			if (newToast.options.autoClose) {
				timeoutsRef.current[`add-idle-${id}`] = setTimeout(() => {
					restateToast(id, 'out');

					timeoutsRef.current[`add-out-${id}`] = setTimeout(() => {
						deleteToast(id);
						delete timeoutsRef.current[`add-out-${id}`];
					}, newToast.options.outDuration ?? 500);

					delete timeoutsRef.current[`add-idle-${id}`];
				}, newToast.options.idleDuration ?? 5000);
			}

			delete timeoutsRef.current[`add-in-${id}`];
		}, newToast.options.inDuration ?? 500);
	}

	function restateToast(id: string, newState: ToastState) {
		setToasts((currentToasts) => {
			const index = currentToasts.findIndex((toast) => toast.id === id);

			if (index >= 0) {
				const newToasts = [...currentToasts];

				newToasts[index] = {
					...newToasts[index],
					options: {
						...newToasts[index].options,
						state: newState,
					},
				};

				return newToasts;
			}

			return currentToasts;
		});
	}

	function closeToast(id) {
		setToasts((currentToasts) => {
			const closingToast = toasts.find((toast) => toast.id === id);

			if (closingToast) {
				restateToast(id, 'out');

				timeoutsRef.current[`close-${id}`] = setTimeout(() => {
					deleteToast(id);

					delete timeoutsRef.current[`close-${id}`];
				}, closingToast.options.outDuration ?? 500);
			}

			return currentToasts;
		});
	}

	function deleteToast(id: string) {
		setToasts((currentToasts) => currentToasts.filter((t) => t.id !== id));
	}

	const value = useMemo(() => ({ toasts, addToast, closeToast }), [toasts]);

	return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
	const { toasts, addToast, closeToast } = useContext(ToastContext);

	const toast: ToastFunction = function generalToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, options);
	} as ToastFunction;

	toast.neutral = function neutralToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, { ...options, type: 'neutral' });
	};

	toast.info = function infoToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, { ...options, type: 'info' });
	};

	toast.success = function successToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, { ...options, type: 'success' });
	};

	toast.warning = function warningToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, { ...options, type: 'warning' });
	};

	toast.error = function errorToast(title: string, message: string, options: ToastOptions = {}) {
		addToast(title, message, { ...options, type: 'error' });
	};

	function clearToasts() {
		toasts.forEach((toast) => {
			closeToast(toast.id);
		});
	}

	return {
		toasts,
		toast,
		closeToast,
		clearToasts,
	};
}

export default ToastContext;
